/**
 * Angular InputFieldWithLabel Component
 *
 * Migrated from the AngularJS `inputFieldWithLabel` attribute directive
 * (apps/b2b-ui/common/directives/inputFieldWithLabel/).
 *
 * Renders a floating-label form field — input + label + optional required
 * asterisk + error message (icon + text, absorbing the legacy `inputFieldError`
 * sub-directive) + optional info note. Implements ControlValueAccessor for use
 * with `[(ngModel)]` / `formControl`, and exposes a `[(model)]` two-way binding
 * for use outside Forms and from the AngularJS downgrade.
 *
 * Optional input masking is supported via Maskito (`@maskito/core`), mirroring
 * the legacy `acme-mask` directive: `maskOptions` is a `MaskitoOptions`, and
 * `maskValue` is the live `{ unmasked, masked }` object that Maskito's
 * postprocessor mutates in place (see common/utils/masks). On every input the
 * model is set from `maskValue.unmasked`.
 *
 * Usage in Angular templates:
 *   <acme-input-field-with-label name="first" label="First Name"
 *                                [(ngModel)]="firstName"
 *                                [errors]="errors" fieldName="first">
 *   </acme-input-field-with-label>
 *
 * Usage in AngularJS templates (after downgrade):
 *   <acme-input-field-with-label name="first" label="First Name"
 *                                model="$ctrl.firstName"
 *                                on-model-change="$ctrl.firstName = $event"
 *                                errors="$ctrl.errors" field-name="first">
 *   </acme-input-field-with-label>
 *
 * Notes on parity with the legacy directive:
 * - `numeric` and `fieldFocus` directive deps are re-implemented inline (non-digit
 *   guard via `(beforeinput)`; focus on AfterViewInit), matching the `inputField`
 *   migration.
 * - The legacy isolate scope declared `icon`, `useCancel`, `useFullWidth` and
 *   `uppercase`, but the template never rendered the first three and no consumer
 *   passes `uppercase`; all four are dropped as dead bindings.
 * - `isPanelGroup` / `groupTypeClass` were read by the controller but never bound
 *   on the isolate scope nor used by the template, so they are dropped too.
 * - The single-space placeholder is preserved — the floating-label CSS relies on
 *   `:placeholder-shown`.
 */
import {
  AfterViewInit, booleanAttribute, Component, computed, ElementRef,
  EventEmitter, forwardRef, Input, numberAttribute, OnDestroy, Output,
  signal, ViewChild,
} from '@angular/core';
import { NgIf } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Maskito, MaskitoOptions, maskitoTransform } from '@maskito/core';

let uniqueId = 0;

interface FieldError {
  message: string;
}

/**
 * The host element acts as the wrapper (replaces the outer
 * `<div class="inputFieldWithLabel wrapper" data-ng-class="{'error generic-input-error': …}">`
 * the legacy AJS directive emitted with `replace: true`), so layout classes
 * passed in by consumers stay on the same element as `inputFieldWithLabel` /
 * `wrapper`, preserving the legacy CSS contract.
 *
 * All host classes are exposed as `[class.x]` bindings (not a static `class`
 * entry) so they are applied via `classList.add/remove` and DO NOT clobber any
 * `class="…"` provided by the consumer — critical under `downgradeComponent`,
 * where AJS sets the element's class attribute before Angular initializes the
 * host bindings.
 */
@Component({
  selector: 'acme-input-field-with-label',
  standalone: true,
  imports: [NgIf],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InputFieldWithLabelComponent), multi: true },
  ],
  template: require('./input-field-with-label.component.html'),
  styles: [require('./input-field-with-label.component.less')],
  host: {
    '[class.inputFieldWithLabel]': 'true',
    '[class.wrapper]': 'true',
    '[class.error]': 'isInvalid()',
    '[class.generic-input-error]': 'isInvalid()',
  },
})
export class InputFieldWithLabelComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input() type = 'text';
  @Input() errorMessage = '';
  @Input() fieldName = '';
  @Input() maxLength: number | null = null;
  @Input({ transform: numberAttribute }) fieldFocusTimeout = 0;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input({ transform: booleanAttribute }) setFieldFocus = false;
  @Input({ transform: booleanAttribute }) hideLabel = false;
  @Input({ transform: booleanAttribute }) required = false;
  @Input({ transform: booleanAttribute }) numericOnly = false;
  @Input({ transform: booleanAttribute }) notifyOnBlur = false;

  @Input() maskOptions: MaskitoOptions | null = null;
  /** Live `{ unmasked, masked }` object mutated in place by Maskito's postprocessor. */
  @Input() maskValue: { unmasked: string; masked: string } | null = null;

  private readonly fallbackName = `acme-input-field-with-label-${++uniqueId}`;

  private readonly _name = signal('');
  @Input()
  get name(): string { return this._name() || this.fallbackName; }
  set name(value: string) { this._name.set(value ?? ''); }

  private readonly _label = signal('');
  @Input()
  get label(): string { return this._label(); }
  set label(value: string) { this._label.set(value ?? ''); }

  private readonly _validTest = signal(true);
  @Input({ transform: booleanAttribute })
  get validTest(): boolean { return this._validTest(); }
  set validTest(value: boolean) { this._validTest.set(value); }

  private readonly _errors = signal<Record<string, FieldError> | null>(null);
  @Input()
  get errors(): Record<string, FieldError> | null { return this._errors(); }
  set errors(value: Record<string, FieldError> | null) { this._errors.set(value ?? null); }

  private readonly _info = signal<string>('');
  @Input()
  get info(): string { return this._info(); }
  set info(value: string | false | null | undefined) { this._info.set(value || ''); }

  private readonly _value = signal('');
  @Input()
  get model(): string { return this._value(); }
  set model(v: string) {
    this._value.set(v ?? '');
    this._display.set(this.maskOptions ? maskitoTransform(v ?? '', this.maskOptions) : (v ?? ''));
  }
  @Output() modelChange = new EventEmitter<string>();

  /** Fires the legacy `model-changed` callback. */
  @Output() modelChanged = new EventEmitter<void>();
  /** Fires the legacy `on-blur` callback. */
  @Output() blurred = new EventEmitter<void>();

  /** Unmasked model value. */
  readonly value = this._value.asReadonly();
  /** What is shown in the input box (masked when a mask is active). */
  private readonly _display = signal('');
  readonly displayValue = this._display.asReadonly();

  /** Error text resolved from the `errors[fieldName]` map (legacy `ctrl.error`). */
  readonly error = computed(() => this._errors()?.[this.fieldName]?.message || '');

  /** Mirrors the legacy `checkValid()`: external `validTest` wins when an `errorMessage` is set. */
  readonly isInvalid = computed(() =>
    this.errorMessage ? !this._validTest() : !!this.error());

  readonly inputId = computed(() => `input-${this._name() || this.fallbackName}`);
  readonly errorId = computed(() => `input-${this.fieldName || this._name() || this.fallbackName}-error`);
  readonly describedById = computed(() => `${this._name() || this.fallbackName}-error-message`);
  /** Resolved error text shown in the message line: explicit `errorMessage` wins. */
  readonly errorText = computed(() => this.errorMessage || this.error());

  @ViewChild('inputEl') inputEl?: ElementRef<HTMLInputElement>;

  private mask: Maskito | null = null;

  private onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  /** Legacy `notifyOnChange()`: change fires the callback only when not deferred to blur. */
  private notifyOnChange(): boolean { return !this.notifyOnBlur; }

  ngAfterViewInit(): void {
    const el = this.inputEl?.nativeElement;

    if (el && this.maskOptions) {
      this.mask = new Maskito(el, this.maskOptions);
    }

    if (this.setFieldFocus) {
      setTimeout(() => el?.focus(), this.fieldFocusTimeout);
    }
  }

  ngOnDestroy(): void {
    this.mask?.destroy();
    this.mask = null;
  }

  onInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    // Echo what the input (post-Maskito) actually shows, so the [value] binding
    // never fights Maskito's cursor handling.
    this._display.set(el.value);
    // With a mask, the model is the unmasked value Maskito wrote into maskValue.
    const next = this.maskOptions ? (this.maskValue?.unmasked ?? '') : el.value;
    this._value.set(next);
    this.modelChange.emit(next);
    this.onChange(next);

    if (this.notifyOnChange()) {
      this.modelChanged.emit();
    }
  }

  onBlur(): void {
    this.onTouched();
    // Legacy onBlur always fires modelChanged, then the onBlur callback.
    this.modelChanged.emit();
    this.blurred.emit();
  }

  onBeforeInput(event: InputEvent): void {
    if (!this.numericOnly) { return; }
    const data = event.data;
    if (data != null && !/^[0-9]+$/.test(data)) {
      event.preventDefault();
    }
  }

  writeValue(value: string): void {
    const v = value ?? '';
    this._value.set(v);
    this._display.set(this.maskOptions ? maskitoTransform(v, this.maskOptions) : v);
  }
  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }
}
